import { useState, useEffect } from "react";

import { useApolloClient, useMutation } from "@apollo/client";

import { FilterMatchMode, FilterOperator } from "primereact/api";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import { BOOKINGS } from "../../../provider/Bookings/query";
import { useUser } from "../../../context/users";
import { CANCEL_BOOKING } from "../../../provider/Bookings/mutation";
import { useSettings } from "../../../context/settings";
import { getGraphsqErros } from "../../../utilities/functions";
import REASONS from "../../../provider/Reasons/query";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";

const Races = () => {
  const settings = useSettings();
  const apollo = useApolloClient();
  const [cancelBooking, { loading: canceling }] = useMutation(CANCEL_BOOKING);

  const user = useUser();
  const franchise_id = user?.getUser()?.franchise?.id;
  const acting_region_id = user?.getUser()?.actingRegion?.id;
  const is_callcenter = user?.getUser()?.is_callcenter;

  const [bookings, setBookings] = useState<any>(null);
  const [reasons, setReasons] = useState<iReason[]>([]);
  const [filters, setFilters] = useState<any>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [reloading, setReloading] = useState<boolean>(false);

  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelBookingId, setCancelBookingId] = useState<any>();
  const [cancelReason, setCancelReason] = useState<any>();

  const [reload, setReload] = useState(0);

  const states = [
    "AWAIT",
    "ACCEPTED_DRIVER",
    "CANCELED_DRIVER",
    "CANCELED_PASSENGER",
    "ON_ROUTE",
    "EMBARKATION",
    "EXPIRED",
    "COMPLETED",
  ];

  useEffect(() => {
    getBookings();

    loadReasons();

    initFilters1();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (reload > 0) getBookings();
  }, [reload]); // eslint-disable-line react-hooks/exhaustive-deps

  const getBookings = () => {
    if (!reloading) setLoading(true);
    const filter: any = { callcenter: true };

    if (franchise_id) filter.franchise_id = Number(franchise_id);
    if (acting_region_id) filter.acting_region_id = Number(acting_region_id);

    if (is_callcenter) filter.passenger_id = user.getUser()?.passenger_id;

    apollo
      .query({
        query: BOOKINGS,
        variables: filter,
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.bookings;
        setBookings(() => mapBookings(data));
        setLoading(false);
        setReloading(false);
        
        setTimeout(() => {
          setReload(reload + 1)
          setReloading(true);
        }, 20000);
      })
      .catch(error => {
        console.log('getBookings error: ', error);
        setLoading(false);
        setReloading(false);
      });
  };

  const mapBookings = (data: any) => {
    return [...data].map((d: any) => {
      return {
        id: d.id,
        driver: d.driver?.name.split(' ')[0],
        phone_driver: d.driver?.phone,
        passenger: d.passenger?.name,
        phone: d.passenger?.phone,
        destiny_address: d.destiny_address,
        origin_address: d.origin_address,
        created_at: new Date(d.created_at),
        distance: d.distance,
        price: d.offered_value_driver || d.original_value,
        state: d.state,
        stops: d.stops,
        service: d.service?.name,
        is_callcenter: d.is_callcenter,
        stopsQuant: d.stops?.data?.length || 0,
      }
    });
  };

  function cancel(booking_id: number, reason_id: number) {
    cancelBooking({
      variables: {
        booking_id: parseInt(`${booking_id}`),
        reason_id: parseInt(`${reason_id}`),
        state: "CANCELED_PASSENGER",
      },
    })
      .then(e => {
        setReloading(true);
        setReload(reload + 1);
        setCancelDialog(false);
        setCancelBookingId(null);
        setCancelReason(null);
      })
      .catch(e => {
        console.log('cancelBooking error: ', JSON.stringify(e));
        settings?.setErrorMessage(getGraphsqErros(e));
        settings?.setVisibleError(true);
      });
  }

  function loadReasons() {
    apollo
      .query({
        query: REASONS,
        variables: { type: "PASSENGER", page: "CANCELLATION" },
        fetchPolicy: "no-cache",
      })
      .then(response => {
        setReasons(response.data.reasons);
      })
      .catch(e => {});
  }

  const initFilters1 = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      "driver.name": { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      "passenger.name": { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      created_at: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
      destiny_address: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      origin_address: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      state: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    });
  };

  const driverBodyTemplate = (rowData: any) => {
    if (!rowData.driver) return '';
    return `${rowData.driver} - Tel: ${rowData.phone_driver}`;
  };

  const passengerBodyTemplate = (rowData: any) => {
    const {taximeter} = rowData.stops || {};
    if (taximeter) return `${taximeter.name}`;

    return `${rowData.passenger}`;
  };

  const originAddressBodyTemplate = (rowData: any) => {
    if (rowData.is_callcenter){
      return `${rowData.origin_address?.split(',')[0]}`;
    };

    return `${rowData.origin_address?.split(' - ')[0]}`;
  };

  const destinyAddressBodyTemplate = (rowData: any) => {
    if (rowData.is_callcenter){
      return `${rowData.destiny_address?.split(',')[0]}`;
    };

    return `${rowData.destiny_address?.split(' - ')[0]}`;
  };

  const distanceBodyTemplate = (rowData: any) => {
    return `${(Number(rowData.distance) / 1000).toFixed(1)}`;
  };

  const priceBodyTemplate = (rowData: any) => {
    return `R$ ${(rowData.price/100).toFixed(2)}`;
  };

  const stateItemTemplate = (state: string) => {
    switch (state) {
      case 'AWAIT':
        return (
          <Tag className="mr-2" value={'BUSCANDO'}/>
        );
      case 'ACCEPTED_DRIVER':
        return (
          <Tag className="mr-2" severity="info" value={'A CAMINHO'}/>
        );
      case 'EMBARKATION':
        return (
          <Tag className="mr-2" style={{backgroundColor: 'blue'}} value={'MOTORISTA CHEGOU'}/>
        );
      case 'CANCELED_DRIVER':
        return (
          <Tag severity="danger" value={'CANCELADA'}/>
        );
      case 'CANCELED_PASSENGER':
        return (
          <Tag severity="danger" value={'CANCELADA'}/>
        );
      case 'ON_ROUTE':
        return (
          <Tag className="mr-2" severity="info" value={'EM ROTA'}/>
        );
      case 'EXPIRED':
        return (
          <Tag severity="danger" value={'EXPIRADO'}/>
        );
      case 'COMPLETED':
        return (
          <Tag className="mr-2" severity="success" value={'FINALIZADA'}/>
        );
    
      default:
        return (
          <Tag className="mr-2" value={state}/>
        );
    }
  };

  const stateBodyTemplate = (rowData: any) => {
    if (rowData.stops?.taximeter) return (
      <Tag className="mr-2" value={'TAXÍMETRO'}/>
    );

    return stateItemTemplate(rowData.state);
  };

  const cancelBodyTemplate = (rowData: any) => {
    if (
      rowData.state !== 'AWAIT' &&
      rowData.state !== 'ACCEPTED_DRIVER' &&
      rowData.state !== 'EMBARKATION'
    ) return null;
    
    return <Button label="CANCELAR" onClick={() => {
      setCancelBookingId(rowData.id);
      setCancelDialog(true);
    }} className="p-button-danger mr-2 mb-2" />
  }

  const stateFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={states}
        onChange={(e: any) => options.filterCallback(e.value, options.index)}
        itemTemplate={stateItemTemplate}
        placeholder="Select a Status"
        className="p-column-filter"
        showClear
      />
    );
  };

  const cancelDialodFooter = (
    <>
      <Button label="Não" icon="pi pi-times" className="p-button-text text-red-500" onClick={() => setCancelDialog(false)} />
      <Button label={canceling ? "Cancelando..." : "Sim, Cancelar!"} disabled={canceling} icon="pi pi-check" className="p-button-text" onClick={() => cancel(cancelBookingId, cancelReason.code)} />
    </>
  );

  return (
    <div className="grid table-demo">
      <div className="col-12">
        <div className="card">
          {loading || reloading ? (
            <h5>
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem', marginRight: 10 }} />
              Atualizando...
            </h5>
          ) : (<h5>Corridas</h5>)}
          <DataTable
            value={bookings}
            paginator
            className="p-datatable-gridlines"
            showGridlines
            rows={10}
            dataKey="id"
            filters={filters}
            filterDisplay="menu"
            loading={loading}
            responsiveLayout="scroll"
            emptyMessage="No customers found.">
            <Column field="id" header="ID" filter filterPlaceholder="Search by id" style={{ minWidth: "12rem" }} />
            <Column
              header="Status"
              filterField="state"
              style={{ minWidth: "10rem" }}
              filter
              body={stateBodyTemplate}
              filterElement={stateFilterTemplate}
            />
            <Column
              header="Motorista"
              filterField="driver"
              style={{ minWidth: "10rem" }}
              filter
              filterPlaceholder="Search by name"
              body={driverBodyTemplate}
            />
            {is_callcenter ? null : (
              <Column
                header="Passageiro"
                filterField="passenger"
                style={{ minWidth: "10rem" }}
                filter
                filterPlaceholder="Search by name"
                body={passengerBodyTemplate}
              />
            )}
            <Column
              header="Origem"
              filterField="origin_address"
              style={{ minWidth: "10rem" }}
              filter
              filterPlaceholder="Search by origin address"
              body={originAddressBodyTemplate}
            />
            <Column
              header="Destino"
              filterField="destiny_address"
              style={{ minWidth: "10rem" }}
              filter
              filterPlaceholder="Search by destiny address"
              body={destinyAddressBodyTemplate}
            />
            <Column
              header="KM"
              filterField="distance"
              style={{ minWidth: "10rem" }}
              body={distanceBodyTemplate}
            />
            <Column
              header="Preço"
              filterField="price"
              style={{ minWidth: "10rem" }}
              body={priceBodyTemplate}
            />
            <Column field="service" header="Serviço" filter filterPlaceholder="Search by service" style={{ minWidth: "12rem" }} />
            <Column
              header="Cancelar"
              style={{ minWidth: "10rem" }}
              body={cancelBodyTemplate}
            />
          </DataTable>

          {/* Modal cancelar booking */}
          <Dialog
            visible={cancelDialog}
            // style={{ width: "450px" }}
            header="Cancelar a corrida?"
            modal
            footer={cancelDialodFooter}
            onHide={() => setCancelDialog(false)}>
            <div className="flex align-items-center justify-content-center">
              <div className="flex flex-column field col-12 md:col-12">
                <label htmlFor="name">Motivo</label>
                <Dropdown
                  value={cancelReason}
                  onChange={e => setCancelReason(e.value)}
                  options={reasons.map(i => ({ code: i.id, name: i.name }))}
                  optionLabel="name"
                  placeholder="Informe o motivo"
                />
              </div>
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Races;
